import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PACKAGE_EVENT_LABELS, type PackagesOverview } from '@azko/shared';
import { api } from '../lib/api.js';
import { formatDelta, formatReceived, formatSeconds, statusLabel, statusTone } from '../lib/format.js';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  LoadingRows,
  PageHeader,
  StatCard,
  StatusBadge,
} from '../components/ui.js';

export function OverviewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['packages', 'overview'],
    queryFn: () => api.get<PackagesOverview>('/packages/overview'),
  });

  return (
    <>
      {/*
        As duas acoes do dia a dia moram na linha do titulo — no DESKTOP.
        Antes ocupavam dois cards grandes explicando o que o produto faz — uma
        dobra inteira de texto que o porteiro logado ja sabe, entre ele e os
        numeros que ele abriu a tela para ver.

        No celular elas somem daqui: a barra inferior ja carrega "Registrar" e
        "Ler QR" em toda tela, e repetir aqui empurraria os indicadores para
        baixo para oferecer o que esta a um polegar de distancia.
      */}
      <PageHeader
        title="Visao geral"
        action={
          <div className="hidden flex-wrap gap-2 sm:flex">
            <Link to="/ler-qr">
              <Button variant="ghost">Ler QR</Button>
            </Link>
            <Link to="/registrar">
              <Button>+ Registrar encomenda</Button>
            </Link>
          </div>
        }
      />

      {/* Indicadores */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatCard
          label="Aguardando retirada"
          value={data?.awaiting ?? '—'}
          hint={data ? `${data.awaitingOver48h} ha mais de 48h` : undefined}
          highlight
        />
        <StatCard
          label="Recebidas hoje"
          value={data?.receivedToday ?? '—'}
          hint={data ? formatDelta(data.receivedToday, data.receivedYesterday) : undefined}
        />
        <StatCard
          label="Retiradas hoje"
          value={data?.pickedUpToday ?? '—'}
          hint={
            data && data.receivedToday > 0
              ? `${Math.round((data.pickedUpToday / data.receivedToday) * 100)}% do total`
              : undefined
          }
        />
        <StatCard
          label="Tempo medio"
          value={formatSeconds(data?.avgConfirmSeconds ?? null)}
          hint="por confirmacao"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {/* Movimentacoes recentes */}
        <Card className="xl:col-span-2">
          <CardHeader
            title="Encomendas recentes"
            action={
              <Link to="/encomendas">
                <Button variant="ghost">Ver todas</Button>
              </Link>
            }
          />

          {isLoading && <LoadingRows />}
          {!isLoading && data?.recent.length === 0 && (
            <EmptyState
              title="Nenhuma encomenda ainda"
              description="Assim que a portaria registrar a primeira entrada ela aparece aqui."
            />
          )}

          {data && data.recent.length > 0 && (
            <ul className="divide-y divide-line-soft border-t border-line-soft">
              {data.recent.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6"
                >
                  <div className="min-w-0">
                    <p className="truncate font-bold">{item.residentName ?? `Unidade ${item.unitLabel}`}</p>
                    <p className="text-sm text-muted">
                      #{item.code} • apto {item.unitLabel}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted">{formatReceived(item.receivedAt)}</span>
                    <StatusBadge tone={statusTone(item.status)}>{statusLabel(item.status)}</StatusBadge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Timeline da ultima encomenda mexida — quem, quando e o que aconteceu. */}
        <Card className="p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight">Ultima movimentacao</h2>
          <p className="mt-1 text-sm text-muted">
            {data?.lastActivity ? `Encomenda #${data.lastActivity.code}` : 'Sem movimento hoje'}
          </p>

          {data?.lastActivity && (
            <ol className="mt-6 space-y-5">
              {data.lastActivity.events.map((event) => (
                <li key={event.id} className="flex gap-3">
                  <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
                  <div>
                    <p className="font-semibold">{PACKAGE_EVENT_LABELS[event.type]}</p>
                    <p className="text-sm text-muted">
                      {new Date(event.createdAt).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {event.detail && ` • ${event.detail}`}
                      {event.actorName && ` • por ${event.actorName}`}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </>
  );
}
